import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Chip,
    CircularProgress,
    Skeleton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    TablePagination
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { ReservationService } from '../../../api/services/reservationService';
import { formatCurrency } from '../../../utils/currency';
import { extractTotalCount } from '../../../components/common/tableStyles';

export default function RateOverridesUI({ tenantId, setNotification }) {
    const [overrides, setOverrides] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [rejectModal, setRejectModal] = useState({ open: false, overrideId: null });
    const [rejectionReason, setRejectionReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [totalCount, setTotalCount] = useState(0);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const fetchOverrides = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await ReservationService.getRateOverrides(tenantId, { page, limit: rowsPerPage });
            const list = Array.isArray(data) ? data : (data?.data || []);
            setOverrides(list);
            setTotalCount(extractTotalCount(data, list));
        } catch (error) {
            console.error("Failed to load rate overrides", error);
            setNotification({
                open: true,
                message: error.response?.data?.message || "Failed to load rate overrides",
                severity: 'error'
            });
        } finally {
            setIsLoading(false);
        }
    }, [tenantId, page, rowsPerPage, setNotification]);

    useEffect(() => {
        fetchOverrides();
    }, [fetchOverrides]);

    const handleApprove = async (overrideId) => {
        try {
            await ReservationService.approveRateOverride(overrideId, tenantId);
            setNotification({ open: true, message: "Rate override approved successfully", severity: "success" });
            fetchOverrides();
        } catch (error) {
            setNotification({
                open: true,
                message: error.response?.data?.message || "Failed to approve override",
                severity: "error"
            });
        }
    };

    const handleReject = async () => {
        if (!rejectionReason.trim()) return;
        setIsSubmitting(true);
        try {
            await ReservationService.rejectRateOverride(rejectModal.overrideId, { rejectionReason }, tenantId);
            setNotification({ open: true, message: "Rate override rejected successfully", severity: "success" });
            setRejectModal({ open: false, overrideId: null });
            setRejectionReason('');
            fetchOverrides();
        } catch (error) {
            setNotification({
                open: true,
                message: error.response?.data?.message || "Failed to reject override",
                severity: "error"
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString();
    };

    const parseReason = (reasonText) => {
        if (!reasonText) return 'N/A';
        try {
            const parsed = JSON.parse(reasonText);
            return parsed.reason || parsed.remarks || reasonText;
        } catch(e) {
            return reasonText;
        }
    };

    return (
        <Box>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Booking ID</TableCell>
                            <TableCell>Guest Name</TableCell>
                            <TableCell>Old Rate</TableCell>
                            <TableCell>New Rate</TableCell>
                            <TableCell>Requested Discount</TableCell>
                            <TableCell>Reason</TableCell>
                            <TableCell>Requested By</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            [1, 2, 3].map((i) => (
                                <TableRow key={i}>
                                    <TableCell><Skeleton width="60%" /></TableCell>
                                    <TableCell><Skeleton width="80%" /></TableCell>
                                    <TableCell><Skeleton width="50%" /></TableCell>
                                    <TableCell><Skeleton width="50%" /></TableCell>
                                    <TableCell><Skeleton width="40%" /></TableCell>
                                    <TableCell><Skeleton width="90%" /></TableCell>
                                    <TableCell><Skeleton width="60%" /></TableCell>
                                    <TableCell><Skeleton width="50%" /></TableCell>
                                    <TableCell align="right"><Skeleton width={80} sx={{ display: 'inline-block' }} /></TableCell>
                                </TableRow>
                            ))
                        ) : overrides.length > 0 ? (
                            overrides.map((override) => (
                                <TableRow key={override.id || override._id} hover>
                                    <TableCell>{override.reservation?.bookingId || override.bookingId || 'N/A'}</TableCell>
                                    <TableCell>{override.guestName || 'N/A'}</TableCell>
                                    <TableCell>{formatCurrency(override.originalRate || override.oldRate)}</TableCell>
                                    <TableCell>{formatCurrency(override.overriddenRate || override.newRate || override.requestedRate)}</TableCell>
                                    <TableCell>
                                        <Typography color="error" variant="body2" fontWeight="bold">
                                            {formatCurrency(Number(override.originalRate || 0) - Number(override.overriddenRate || 0))}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{parseReason(override.reasonText || override.reason)}</TableCell>
                                    <TableCell>{override.requestedBy?.name || override.requestedBy || 'N/A'} <br/> <Typography variant="caption" color="text.secondary">{formatDate(override.createdAt)}</Typography></TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={override.status || 'Pending'} 
                                            size="small" 
                                            color={
                                                override.status === 'Approved' ? 'success' : 
                                                override.status === 'Rejected' ? 'error' : 
                                                'warning'
                                            } 
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        {(override.status === 'Pending' || !override.status) ? (
                                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    size="small"
                                                    startIcon={<CheckCircleIcon />}
                                                    onClick={() => handleApprove(override.id || override._id)}
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    Approve
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    size="small"
                                                    startIcon={<CancelIcon />}
                                                    onClick={() => setRejectModal({ open: true, overrideId: override.id || override._id })}
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    Reject
                                                </Button>
                                            </Box>
                                        ) : (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pt: 1, fontStyle: 'italic' }}>
                                                Actioned
                                            </Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                                    <Typography variant="body1" color="text.secondary">No pending rate overrides found.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[20, 50, 100]}
                    component="div"
                    count={overrides.length === 0 ? 0 : totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>

            {/* Rejection Modal */}
            <Dialog open={rejectModal.open} onClose={() => setRejectModal({ open: false, overrideId: null })} maxWidth="sm" fullWidth>
                <DialogTitle>Reject Rate Override</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Please provide a reason for rejecting this discount override request.
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Rejection Reason"
                        fullWidth
                        multiline
                        rows={3}
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        required
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectModal({ open: false, overrideId: null })} color="inherit">
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleReject} 
                        color="error" 
                        variant="contained" 
                        disabled={!rejectionReason.trim() || isSubmitting}
                    >
                        {isSubmitting ? <CircularProgress size={24} /> : 'Confirm Rejection'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
